import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Film, Tv2, Upload, ChevronRight, Eye, Loader2, Package } from 'lucide-react';

interface RecentWork {
  id: string;
  type: 'movie' | 'tv_show';
  title: string;
  status: string;
  poster_url: string | null;
  view_count: number;
}

interface Stats {
  totalMovies: number;
  totalSeries: number;
  liveContent: number;
  pendingAssets: number;
}

export default function ContentManagement() {
  const { publisher } = useAuth();
  const navigate = useNavigate();
  const [recentWorks, setRecentWorks] = useState<RecentWork[]>([]);
  const [stats, setStats] = useState<Stats>({ totalMovies: 0, totalSeries: 0, liveContent: 0, pendingAssets: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publisher?.id) fetchData();
  }, [publisher?.id]);

  const fetchData = async () => {
    if (!publisher?.id) return;
    setLoading(true);
    try {
      const [worksRes, pendingRes] = await Promise.all([
        supabase
          .from('works')
          .select('id, type, title, status, poster_url, view_count')
          .eq('publisher_id', publisher.id)
          .neq('status', 'archived')
          .order('created_at', { ascending: false })
          .limit(6),
        supabase
          .from('pending_uploads')
          .select('id, upload_status')
          .eq('publisher_id', publisher.id),
      ]);

      const works = (worksRes.data || []) as RecentWork[];
      const pending = pendingRes.data || [];

      setRecentWorks(works);
      setStats({
        totalMovies: works.filter(w => w.type === 'movie').length,
        totalSeries: works.filter(w => w.type === 'tv_show').length,
        liveContent: works.filter(w => w.status === 'published').length,
        pendingAssets: (pending as { upload_status: string }[]).filter(p =>
          !['ingested', 'error'].includes(p.upload_status)
        ).length,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Content Library</h1>
          <p className="text-muted-foreground mt-1">Your movies, series, and upload pipeline</p>
        </div>
        <Button onClick={() => navigate('/content/new')}>
          <Upload className="h-4 w-4 mr-2" />
          New Upload
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="partner-card cursor-pointer hover:border-primary/40 transition-colors" onClick={() => navigate('/content/movies')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Film className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Movies</p>
                <p className="text-2xl font-bold">{stats.totalMovies}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="partner-card cursor-pointer hover:border-primary/40 transition-colors" onClick={() => navigate('/content/series')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Tv2 className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">TV Series</p>
                <p className="text-2xl font-bold">{stats.totalSeries}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="partner-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Eye className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Live</p>
                <p className="text-2xl font-bold">{stats.liveContent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="partner-card cursor-pointer hover:border-primary/40 transition-colors" onClick={() => navigate('/content/assets')}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Package className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Pipeline</p>
                <p className="text-2xl font-bold">{stats.pendingAssets}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          className="partner-card hover:border-primary/40 transition-colors cursor-pointer"
          onClick={() => navigate('/content/movies')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Film className="h-5 w-5 text-blue-500" />
                <CardTitle className="text-base">Movies</CardTitle>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Manage individual films, edit metadata, and track performance.
            </p>
          </CardContent>
        </Card>

        <Card
          className="partner-card hover:border-primary/40 transition-colors cursor-pointer"
          onClick={() => navigate('/content/series')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tv2 className="h-5 w-5 text-purple-500" />
                <CardTitle className="text-base">TV Series</CardTitle>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Manage shows, seasons, and episodes. Add new episodes to existing series.
            </p>
          </CardContent>
        </Card>

        <Card
          className="partner-card hover:border-primary/40 transition-colors cursor-pointer"
          onClick={() => navigate('/content/assets')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">Asset Pipeline</CardTitle>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Track uploads through the review pipeline from draft to live.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent content */}
      {recentWorks.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Content</h2>
          </div>
          <div className="space-y-2">
            {recentWorks.map(work => (
              <Card key={work.id} className="partner-card kinora-editorial-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {work.poster_url ? (
                      <img
                        src={work.poster_url}
                        alt={work.title}
                        className="h-12 w-8 rounded object-cover shrink-0"
                      />
                    ) : (
                      <div className="h-12 w-8 rounded bg-muted flex items-center justify-center shrink-0">
                        {work.type === 'movie'
                          ? <Film className="h-3.5 w-3.5 text-muted-foreground" />
                          : <Tv2 className="h-3.5 w-3.5 text-muted-foreground" />
                        }
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{work.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {work.type === 'movie' ? 'Movie' : 'TV Series'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Eye className="h-3.5 w-3.5" />
                        <span>{(work.view_count || 0).toLocaleString()}</span>
                      </div>
                      <Badge variant="outline" className="capitalize">{work.status}</Badge>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={work.type === 'movie' ? `/content/movies/${work.id}` : `/content/series/${work.id}`}>
                          Manage
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {recentWorks.length === 0 && (
        <Card className="partner-card border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">No content yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Upload your first film or create a TV series to get started.
              </p>
            </div>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/content/new')}>
                <Upload className="h-4 w-4 mr-2" />
                Upload Content
              </Button>
              <Button variant="outline" onClick={() => navigate('/content/series/new')}>
                <Tv2 className="h-4 w-4 mr-2" />
                Create Series
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
